import { Project } from "./project-model";
import { User } from "./user-model";

/**
 * CheckDTO – Backend-API-Response für GET /api/check/{id}
 *
 * Pflichtfelder entsprechen der echten API-Antwort.
 * Optionale Felder werden im Mock-Betrieb (useMocks=true) befüllt,
 * um die bestehende UI-Logik zu stützen bis alle Endpunkte migriert sind.
 */
export interface CheckDTO {
  // --- Neue API-Felder (echte Backend-Antwort) ---
  guid: string;
  createdAt: string;
  description: string;
  doneAt: string | null;

  // --- Übergangsfelder für Mock-Betrieb (werden später entfernt) ---
  displayName?: string;
  projectCode?: string;
  status?: CheckStatus;
  reworkStatus?: ReworkStatus;
  /** OID des Erstellers */
  creator?: string;
  /** OID von Prüfer 1-1 */
  checker11?: string;
  /** OID von Prüfer 1-2 */
  checker12?: string;
  /** OID von Prüfer 2 */
  checker2?: string;
  dueDate1?: string;
  /** @deprecated – Fälligkeitsdatum K1-2, wird durch CheckStatusUpdateRequest abgelöst */
  dueDate12?: string | null;
  dueDate2?: string | null;
  sharepointFolderPath?: string;
  directoryPFolderPath?: string;
  isConsultant?: boolean;
}

/** CheckStageDTO – Backend-API-Response für GET /api/check/{id}/stage */
export interface CheckStageDTO {
  id: number;
  /**
   * Eingebettetes Assignee-Objekt (neue API-Antwort).
   * Ersetzt schrittweise das flache assigneeId-Feld.
   */
  assignee?: { oid: string; displayName: string } | null;
  /** OID des zugewiesenen Prüfers (null wenn noch nicht zugewiesen) – DB-Spalte: assignee_id */
  assigneeId?: string | null;
  assignedAt?: string | null;
  createdAt: string;
  /** z.B. "CREATED", "DONE", "REVISED", "REWORK_REQUIRED", "CHECK_IMPOSSIBLE", "CANCELLED" */
  status: string;
  /** z.B. "FIRST" (K1-1), "SECOND" (K1-2), "REVIEW" (K2) */
  phase: string;
  completedAt?: string | null;
  dueAt?: string | null;
}

/**
 * CheckCheckStageAggregate – Backend-API-Response für
 * GET /document/{id}/check und POST /document/{id}/check
 */
export interface CheckCheckStageAggregate {
  guid: string;
  description: string;
  createdAt: string;
  doneAt: string | null;
  stages: CheckStageDTO[];
}

/** Check – Frontend-Domain-Modell (angereichert mit aufgelösten Referenzen) */
export interface Check {
  id: string;
  displayName: string;
  description: string;
  createdAt: string;
  doneAt: string | null;
  project: Project;
  status: CheckStatus;
  reworkStatus: ReworkStatus;
  creator: User | null;
  dueDate1: Date;
  /** Fälligkeitsdatum K1-2 (SECOND-Stage) – optional */
  dueDate12: Date | null;
  dueDate2: Date | null;
  checker11: User | null;
  checker12: User | null;
  checker2: User | null;
  sharepointFolderPath: string;
  directoryPFolderPath: string;
  isConsultant: boolean;
}

export enum CheckStatus {
  "Kontrolle 1-1" = 0,
  "Kontrolle 1-2" = 1,
  "Kontrolle 2" = 2,
  "Abgeschlossen" = 3,
}

export enum ReworkStatus {
  None = 0,
  Wiedervorlage = 1,
  PruefungNichtMoeglich = 2,
}

export function stageStatusToReworkStatus(status: CheckStageStatus): ReworkStatus {
  switch (status) {
    case 'REWORK_REQUIRED':  return ReworkStatus.Wiedervorlage;
    case 'CHECK_IMPOSSIBLE': return ReworkStatus.PruefungNichtMoeglich;
    default:                 return ReworkStatus.None; // CREATED, REVISED, CANCELLED, DONE
  }
}

// --- Backend API DTOs (Request Payloads) ---

/**
 * CheckCreateRequest – Payload für POST /check (spec: CheckStub).
 * Ersetzt das veraltete CheckCreateDTO.
 */
export interface CheckCreateRequest {
  description?: string | null;
  doneAt?: string | null;
}

/**
 * CheckUpdateRequest – Payload für PUT /check/{id} (spec: CheckStub).
 * Ersetzt das veraltete CheckUpdateDTO.
 */
export interface CheckUpdateRequest {
  description?: string | null;
  doneAt?: string | null;
  // --- Übergangsfelder für Mock-Betrieb (werden nach vollständiger API-Migration entfernt) ---
  displayName?: string;
  sharepointFolderPath?: string;
  directoryPFolderPath?: string;
  /** @deprecated – Wird durch CheckStatusUpdateRequest abgelöst */
  dueDate1?: string;
  /** @deprecated – Wird durch CheckStatusUpdateRequest abgelöst */
  dueDate2?: string | null;
  /** @deprecated – Wird durch Stage-Assignee abgelöst */
  isConsultant?: boolean;
}

/**
 * CheckStatusUpdateRequest – Payload für PUT /check/{id}/status.
 * Setzt die Prüfer-OIDs und Fälligkeitsdaten für alle Stages.
 */
export interface CheckStatusUpdateRequest {
  /** Fälligkeitsdatum K1-1 (Pflicht für echte API-Calls) */
  dueDate1?: string;
  /** Fälligkeitsdatum K1-2 (optional) */
  dueDate12?: string | null;
  /** Fälligkeitsdatum K2 (optional) */
  dueDate2?: string | null;
  /** Prüfer-OID K1-1 (Pflicht für echte API-Calls) */
  checkerOid1?: string;
  /** Prüfer-OID K1-2 (optional) */
  checkerOid12?: string | null;
  /** Prüfer-OID K2 (optional) */
  checkerOid2?: string | null;
  // --- Übergangsfelder für Mock-Betrieb (Status-Übergänge, werden nach vollständiger Migration entfernt) ---
  /** @deprecated – Statusübergang über Mock; Spec nutzt /stage-Endpunkte */
  status?: CheckStatus;
  /** @deprecated – Rework-Statusübergang über Mock */
  reworkStatus?: ReworkStatus;
}

/**
 * CheckStageStatus – Backend-Enum für den Status einer einzelnen Stage.
 * Entspricht dem C#-Enum CheckStatus im Backend (nicht zu verwechseln mit dem
 * Frontend-CheckStatus-Enum, der die Prüfphasen abbildet).
 */
export type CheckStageStatus =
  | 'CREATED'
  | 'DONE'
  | 'REVISED'
  | 'REWORK_REQUIRED'
  | 'CHECK_IMPOSSIBLE'
  | 'CANCELLED';

/**
 * CheckStageUpdateRequest – Payload für PUT /check/{checkId}/stage/{stageId}.
 * Entspricht dem Backend-Modell CheckStageStub.
 */
export interface CheckStageUpdateRequest {
  /** Stage-Status (Backend-Enum: CREATED, DONE, REVISED, REWORK_REQUIRED, CHECK_IMPOSSIBLE, CANCELLED) */
  status: CheckStageStatus;
  /** Stage-Phase (Backend-Enum: FIRST, SECOND, REVIEW, UNDEFINED) */
  phase: string;
  /** Abschlusszeitpunkt (ISO-String, optional) – Backend-Feld: completedAt */
  completedAt?: string | null;
  /** Fälligkeitsdatum (ISO-String, optional) */
  dueAt?: string | null;
}

/** @deprecated Verwende CheckCreateRequest */
export interface CheckCreateDTO {
  displayName: string;
  projectCode: string;
  dueDate1: string;
  dueDate2?: string | null;
  checker11: string;
  checker12?: string | null;
  checker2?: string | null;
  sharepointFolderPath?: string;
  directoryPFolderPath?: string;
  isConsultant?: boolean;
}

/** @deprecated Verwende CheckUpdateRequest */
export interface CheckUpdateDTO {
  displayName?: string;
  dueDate1?: string;
  dueDate2?: string | null;
  checker11?: string;
  checker12?: string | null;
  checker2?: string | null;
  sharepointFolderPath?: string;
  directoryPFolderPath?: string;
  isConsultant?: boolean;
}

/** @deprecated Verwende CheckStatusUpdateRequest */
export interface CheckStatusUpdateDTO {
  status: CheckStatus;
  reworkStatus?: ReworkStatus;
}

/** @deprecated – Frontend-internes Modell, nicht spec-aligned */
export interface CheckStub {
  displayName: string;
  dueDate1: string;
  checker11: string;
}

/** Hilfsfunktion: Mappt API-Phase-String auf CheckStatus-Enum */
export function phaseToCheckStatus(phase: string): CheckStatus {
  switch (phase) {
    case 'FIRST':  return CheckStatus['Kontrolle 1-1'];
    case 'SECOND': return CheckStatus['Kontrolle 1-2'];
    case 'REVIEW': return CheckStatus['Kontrolle 2'];
    default:       return CheckStatus['Kontrolle 1-1'];
  }
}
